import { defineStore } from "pinia";
import { ref, reactive, computed } from "vue";
import svc from "@/services/tasks.service";
import { toast } from "@/utils/toast";
import { createScopedCache } from "./_cache";

const taskListCache = createScopedCache("tasks:list");
const taskDetailCache = createScopedCache("tasks:detail");
const TASK_LIST_TTL = 90 * 1000;
const TASK_DETAIL_TTL = 5 * 60 * 1000;

function toYMD(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt)) return String(d).slice(0, 10);
  return dt.toISOString().slice(0, 10);
}
function prettyDate(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt)) return String(d);
  return dt.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export const useTaskStore = defineStore("task", () => {
  const items = ref([]);
  const byId = ref(Object.create(null));
  const loading = ref(false);
  const deletingId = ref(null);
  const meta = ref({ total: 0, per_page: 25, current_page: 1 });

  const filters = reactive({
    q: "",
    project_id: "",
    status: "",
    priority: "",
    tag: "",
    due_from: "",
    due_to: "",
    scope: "", // all|me
    sort: "created", // created|due|priority
    page: 1,
    per_page: 25,
  });

  const hasMore = computed(() => {
    // naive: if we've loaded items < total, there are more
    return items.value.length < (meta.value.total || 0);
  });

  function normalize(raw = {}) {
    const assignees = Array.isArray(raw.assignees) ? raw.assignees : [];
    const avatars = assignees
      .slice(0, 3)
      .map((a) => a.avatar_url || a.avatar || null);
    const extra = Math.max(0, assignees.length - 3);

    return {
      id: raw.id,
      title: raw.title,
      project_id: raw.project_id ?? raw.project?.id ?? null,
      project_name: raw.project?.name || "",
      status: raw.status || "Pending",
      priority: raw.priority || "Medium",
      visibility: raw.visibility || "Private",
      due_date: toYMD(raw.due_date),
      created_pretty: toYMD(raw.created_at),
      due_pretty: prettyDate(raw.due_date),
      tags: raw.tags || [],
      description: raw.description || "",

      board_id: raw.board_id ?? null,
      progress: Number.isFinite(raw.progress) ? raw.progress : 0,

      assignee_ids: assignees.map((a) => a.id),
      assignee_avatars: avatars,
      assignee_extra: extra,

      created_at: raw.created_at,
      updated_at: raw.updated_at,
    };
  }

  async function list(extra = {}) {
    loading.value = true;
    try {
      const params = { ...filters, ...extra };
      const append = !!extra.append;
      if (append) {
        const { data } = await svc.list(params);
        const arr = data?.data || data || [];
        const mapped = arr.map(normalize);
        const existingIds = new Set(items.value.map((x) => x.id));
        mapped.forEach((n) => (byId.value[n.id] = n));
        const toAdd = mapped.filter((n) => !existingIds.has(n.id));
        items.value = items.value.concat(toAdd);
        meta.value = {
          total: data?.meta?.total ?? arr.length,
          per_page: data?.meta?.per_page ?? params.per_page ?? 25,
          current_page: data?.meta?.current_page ?? params.page ?? 1,
        };
      } else {
        const payload = await taskListCache.get(params, {
          ttlMs: TASK_LIST_TTL,
          force: extra.force === true,
          loader: async () => {
            const { data } = await svc.list(params);
            const arr = data?.data || data || [];
            return {
              items: arr.map(normalize),
              meta: {
                total: data?.meta?.total ?? arr.length,
                per_page: data?.meta?.per_page ?? params.per_page ?? 25,
                current_page: data?.meta?.current_page ?? params.page ?? 1,
              },
            };
          },
          onHydrate: ({ items: rows, meta: pageMeta }) => {
            items.value = rows || [];
            byId.value = Object.create(null);
            items.value.forEach((n) => (byId.value[n.id] = n));
            meta.value = pageMeta || meta.value;
          },
        });
        items.value = payload.items || [];
        byId.value = Object.create(null);
        items.value.forEach((n) => (byId.value[n.id] = n));
        meta.value = payload.meta || meta.value;
      }
      filters.page = meta.value.current_page;
      filters.per_page = meta.value.per_page;
    } catch (e) {
      toast(
        e?.response?.data?.message || e.message || "Failed to load tasks",
        "error"
      );
    } finally {
      loading.value = false;
    }
  }

  async function ensureList(extra = {}) {
    return await list({ ...extra, force: false });
  }

  async function get(id) {
    const payload = await taskDetailCache.get(
      { id: Number(id) || id },
      {
        ttlMs: TASK_DETAIL_TTL,
        loader: async () => {
          const { data } = await svc.get(id);
          return { task: normalize(data?.data || data) };
        },
        onHydrate: ({ task }) => {
          if (!task) return;
          byId.value[task.id] = task;
          const i = items.value.findIndex((x) => x.id === task.id);
          if (i === -1) items.value.unshift(task);
          else items.value.splice(i, 1, task);
        },
      }
    );
    const n = payload.task;
    byId.value[n.id] = n;
    const i = items.value.findIndex((x) => x.id === n.id);
    if (i === -1) items.value.unshift(n);
    else items.value.splice(i, 1, n);
    return n;
  }

  async function ensureOne(id) {
    return await get(id);
  }

  function invalidateTaskCaches(id = null) {
    taskListCache.clear();
    if (id != null) taskDetailCache.invalidate({ id: Number(id) || id });
    else taskDetailCache.clear();
  }

  async function create(payload) {
    const { data } = await svc.create(payload);
    const n = normalize(data?.data || data);
    invalidateTaskCaches();
    items.value.unshift(n);
    byId.value[n.id] = n;
    toast("Task created", "success");
    return n;
  }

  async function update(id, payload) {
    const { data } = await svc.update(id, payload);
    const n = normalize(data?.data || data);
    invalidateTaskCaches(id);
    byId.value[n.id] = n;
    const i = items.value.findIndex((x) => x.id === n.id);
    if (i === -1) items.value.unshift(n);
    else items.value.splice(i, 1, n);
    toast("Task updated", "success");
    return n;
  }

  async function remove(id) {
    try {
      deletingId.value = id;
      await svc.remove(id);
      invalidateTaskCaches(id);
      items.value = items.value.filter((x) => x.id !== id);
      delete byId.value[id];
      toast("Deleted", "success");
    } finally {
      deletingId.value = null;
    }
  }

  async function complete(id) {
    const { data } = await svc.complete(id);
    const n = normalize(data?.data || data);
    invalidateTaskCaches(id);
    byId.value[n.id] = n;
    const i = items.value.findIndex((x) => x.id === n.id);
    if (i !== -1) items.value.splice(i, 1, n);
    toast("Marked as completed", "success");
    return n;
  }

  async function downloadReport(format = "xlsx", extra = {}) {
    try {
      const params = { ...filters, ...extra };
      const res = await svc.exportReport(format, params);

      const mime =
        format === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const ext = format === "pdf" ? "pdf" : "xlsx";
      link.setAttribute(
        "download",
        `task-report-${new Date().toISOString().slice(0, 10)}.${ext}`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const msg =
        e?.response?.data?.message || e.message || "Failed to export report";
      toast(msg, "error");
      throw e;
    }
  }

  return {
    items,
    byId,
    loading,
    deletingId,
    meta,
    filters,
    list,
    ensureList,
    get,
    ensureOne,
    create,
    update,
    remove,
    complete,
    hasMore,
    downloadReport,
    invalidateTaskCaches,
  };
});
